// src/utils/chatPersistence.js
// Chat persistence for guests (sessionStorage) and logged-in users (Supabase)
// FIXED: Added sanitization to prevent React Error #31

import { supabase } from '../supabaseClient';

// ============================================
// CONSTANTS
// ============================================

const STORAGE_KEY = 'flightsearch_chat';
const MAX_MESSAGES = 20;
const EXPIRY_HOURS = 24;

// Safe text helper - ensures all values are strings
const safeText = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (value.text) return String(value.text);
    if (value.message) return String(value.message);
    if (value.content) return String(value.content);
    if (value.name) return String(value.name);
    if (value.city) return String(value.city);
    if (value.code) return String(value.code);
    return fallback;
  }
  return String(value);
};

// Sanitize a message object to ensure all fields are safe
const sanitizeMessage = (msg) => {
  if (!msg || typeof msg !== 'object') return null;
  return {
    id: msg.id || Date.now(),
    type: safeText(msg.type, 'ai'),
    text: safeText(msg.text, ''),
    timestamp: safeText(msg.timestamp, new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })),
    // Preserve searchLinks if present (they're URLs, not rendered directly)
    ...(msg.searchLinks ? { searchLinks: msg.searchLinks } : {})
  };
};

// Sanitize flightInfo to ensure all fields are safe
const sanitizeFlightInfo = (info) => {
  if (!info || typeof info !== 'object') return DEFAULT_FLIGHT_INFO;
  return {
    from: safeText(info.from, ''),
    to: safeText(info.to, ''),
    departDate: safeText(info.departDate, ''),
    returnDate: safeText(info.returnDate, ''),
    passengers: Number(info.passengers) || 1,
    class: safeText(info.class, 'economy'),
    tripType: safeText(info.tripType, 'oneway'),
    maxPrice: info.maxPrice ? Number(info.maxPrice) : null,
    dateRange: {
      start: safeText(info.dateRange?.start, ''),
      end: safeText(info.dateRange?.end, '')
    },
    sortPreference: safeText(info.sortPreference, 'best'),
    flexible: Boolean(info.flexible)
  };
};

// Default welcome messages (same as original App.jsx)
export const DEFAULT_MESSAGES = [
  {
    id: 1,
    type: 'ai',
    text: "Hi! I'm your AI travel agent, and I'm here to help you find the perfect flight!",
    timestamp: '12:13 PM'
  },
  {
    id: 2,
    type: 'ai',
    text: 'You can tell me your trip details however you like! For example:\n\n🎯 All at once: "I want to fly from New York to London on Dec 15, returning Dec 22, 2 passengers, economy"\n\n🎯 Or just start: "London on Dec 23rd"\n\nI\'ll figure out what you mean and ask about anything I\'m missing. What\'s your trip?',
    timestamp: '12:13 PM'
  }
];

// Default flight info state
export const DEFAULT_FLIGHT_INFO = {
  from: '',
  to: '',
  departDate: '',
  returnDate: '',
  passengers: 1,
  class: 'economy',
  tripType: 'oneway',
  maxPrice: null,
  dateRange: { start: '', end: '' },
  sortPreference: 'best',
  flexible: false
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if timestamp is older than EXPIRY_HOURS
 */
const isExpired = (timestamp) => {
  if (!timestamp) return true;
  const savedTime = new Date(timestamp).getTime();
  const now = Date.now();
  const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
  return hoursDiff > EXPIRY_HOURS;
};

/**
 * Trim messages to keep only the last MAX_MESSAGES
 */
export const trimMessages = (messages) => {
  if (!Array.isArray(messages)) return DEFAULT_MESSAGES;
  if (messages.length <= MAX_MESSAGES) return messages;
  return messages.slice(-MAX_MESSAGES);
};

/**
 * Get current timestamp in ISO format
 */
const getCurrentTimestamp = () => new Date().toISOString();

// ============================================
// GUEST (sessionStorage) FUNCTIONS
// ============================================

/**
 * Save chat to sessionStorage (for guests)
 */
const saveToSession = (messages, flightInfo, conversationHistory = []) => {
  try {
    const data = {
      messages: trimMessages(messages),
      flightInfo: flightInfo || DEFAULT_FLIGHT_INFO,
      conversationHistory: conversationHistory.slice(-10), // Keep last 10 conversation turns
      savedAt: getCurrentTimestamp()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    console.log('💾 Chat saved to sessionStorage');
    return true;
  } catch (error) {
    console.error('❌ Failed to save to sessionStorage:', error);
    return false;
  }
};

/**
 * Load chat from sessionStorage (for guests)
 * FIXED: Sanitizes all loaded data to prevent React Error #31
 */
const loadFromSession = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      console.log('ℹ️ No saved chat in sessionStorage');
      return null;
    }
    
    const data = JSON.parse(stored);
    
    // Check if expired
    if (isExpired(data.savedAt)) {
      console.log('⏰ Chat expired (>24h), clearing...');
      clearSession();
      return null;
    }
    
    // SANITIZE all loaded data
    const sanitizedMessages = Array.isArray(data.messages) 
      ? data.messages.map(sanitizeMessage).filter(Boolean)
      : DEFAULT_MESSAGES;
    
    const sanitizedFlightInfo = sanitizeFlightInfo(data.flightInfo);
    
    console.log('✅ Chat loaded from sessionStorage (sanitized)');
    return {
      messages: sanitizedMessages.length > 0 ? sanitizedMessages : DEFAULT_MESSAGES,
      flightInfo: sanitizedFlightInfo,
      conversationHistory: data.conversationHistory || []
    };
  } catch (error) {
    console.error('❌ Failed to load from sessionStorage:', error);
    // Clear corrupted data
    clearSession();
    return null;
  }
};

/**
 * Clear sessionStorage chat
 */
const clearSession = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    console.log('🗑️ sessionStorage chat cleared');
    return true;
  } catch (error) {
    console.error('❌ Failed to clear sessionStorage:', error);
    return false;
  }
};

// ============================================
// LOGGED-IN USER (Supabase) FUNCTIONS
// ============================================

/**
 * Save chat to Supabase (for logged-in users)
 */
const saveToSupabase = async (userId, messages, flightInfo, conversationHistory = []) => {
  try {
    if (!userId) {
      console.error('❌ No userId provided for Supabase save');
      return false;
    }

    const payload = {
      user_id: userId,
      messages: trimMessages(messages),
      search_params: {
        flightInfo: flightInfo || DEFAULT_FLIGHT_INFO,
        conversationHistory: conversationHistory.slice(-10)
      },
      updated_at: getCurrentTimestamp()
    };

    // Upsert: update if exists, insert if not
    const { error } = await supabase
      .from('conversations')
      .upsert(payload, { 
        onConflict: 'user_id',
        ignoreDuplicates: false 
      });

    if (error) {
      // If upsert fails due to no unique constraint, try insert/update separately
      if (error.code === '42P10' || error.message.includes('unique')) {
        // Try to find existing and update
        const { data: existing } = await supabase
          .from('conversations')
          .select('id')
          .eq('user_id', userId)
          .single();

        if (existing) {
          const { error: updateError } = await supabase
            .from('conversations')
            .update({
              messages: payload.messages,
              search_params: payload.search_params,
              updated_at: payload.updated_at
            })
            .eq('user_id', userId);

          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from('conversations')
            .insert(payload);

          if (insertError) throw insertError;
        }
      } else {
        throw error;
      }
    }

    console.log('💾 Chat saved to Supabase');
    return true;
  } catch (error) {
    console.error('❌ Failed to save to Supabase:', error);
    return false;
  }
};

/**
 * Load chat from Supabase (for logged-in users)
 * FIXED: Sanitizes all loaded data to prevent React Error #31
 */
const loadFromSupabase = async (userId) => {
  try {
    if (!userId) {
      console.log('ℹ️ No userId provided for Supabase load');
      return null;
    }

    const { data, error } = await supabase
      .from('conversations')
      .select('messages, search_params, updated_at')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found - this is okay for new users
        console.log('ℹ️ No saved conversation in Supabase');
        return null;
      }
      throw error;
    }

    // Check if expired
    if (isExpired(data.updated_at)) {
      console.log('⏰ Conversation expired (>24h), starting fresh...');
      return null;
    }

    // SANITIZE all loaded data
    const sanitizedMessages = Array.isArray(data.messages) 
      ? data.messages.map(sanitizeMessage).filter(Boolean)
      : DEFAULT_MESSAGES;
    
    const sanitizedFlightInfo = sanitizeFlightInfo(data.search_params?.flightInfo);

    console.log('✅ Chat loaded from Supabase (sanitized)');
    return {
      messages: sanitizedMessages.length > 0 ? sanitizedMessages : DEFAULT_MESSAGES,
      flightInfo: sanitizedFlightInfo,
      conversationHistory: data.search_params?.conversationHistory || []
    };
  } catch (error) {
    console.error('❌ Failed to load from Supabase:', error);
    return null;
  }
};

/**
 * Clear Supabase conversation (optional - for "New Chat" feature)
 */
const clearSupabase = async (userId) => {
  try {
    if (!userId) return false;

    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('user_id', userId);

    if (error) throw error;
    
    console.log('🗑️ Supabase conversation cleared');
    return true;
  } catch (error) {
    console.error('❌ Failed to clear Supabase:', error);
    return false;
  }
};

// ============================================
// MAIN EXPORTED FUNCTIONS
// ============================================

/**
 * Save chat - automatically chooses storage based on auth state
 * @param {Array} messages - Chat messages array
 * @param {Object} flightInfo - Current flight search parameters
 * @param {Array} conversationHistory - AI conversation history
 * @param {string|null} userId - User ID if logged in, null for guests
 */
export const saveChat = async (messages, flightInfo, conversationHistory, userId) => {
  if (userId) {
    return await saveToSupabase(userId, messages, flightInfo, conversationHistory);
  } else {
    return saveToSession(messages, flightInfo, conversationHistory);
  }
};

/**
 * Load chat - automatically chooses storage based on auth state
 * @param {string|null} userId - User ID if logged in, null for guests
 * @returns {Object|null} - { messages, flightInfo, conversationHistory } or null
 */
export const loadChat = async (userId) => {
  if (userId) {
    return await loadFromSupabase(userId);
  } else {
    return loadFromSession();
  }
};

/**
 * Clear chat - clears from appropriate storage
 * @param {string|null} userId - User ID if logged in, null for guests
 */
export const clearChat = async (userId) => {
  if (userId) {
    return await clearSupabase(userId);
  } else {
    return clearSession();
  }
};

/**
 * Migrate guest chat to Supabase when user logs in
 * @param {string} userId - The newly logged in user's ID
 */
export const migrateGuestChatToUser = async (userId) => {
  try {
    const guestChat = loadFromSession();
    
    if (guestChat && guestChat.messages.length > DEFAULT_MESSAGES.length) {
      // Guest has meaningful chat history, migrate it
      console.log('🔄 Migrating guest chat to user account...');
      await saveToSupabase(userId, guestChat.messages, guestChat.flightInfo, guestChat.conversationHistory);
      clearSession(); // Clear sessionStorage after migration
      console.log('✅ Guest chat migrated successfully');
      return guestChat;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Failed to migrate guest chat:', error);
    return null;
  }
};

/**
 * Check if there's a saved chat (useful for showing "Continue chat?" prompt)
 * @param {string|null} userId - User ID if logged in, null for guests
 */
export const hasSavedChat = async (userId) => {
  const chat = await loadChat(userId);
  return chat !== null && chat.messages.length > DEFAULT_MESSAGES.length;
};
